import { FC, Fragment, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import dayjs, { Dayjs } from "utils/dayjs"

import Modal from "components/Modal/Modal"
import { Flex } from "components/Flex"
import Button from "components/Button/Button"
import toastMessages from "stores/toastMessages"
import useInputNumber from "components/InputNumber/hooks/useInputNumber"
import OperationHoursRecord, { ICreateOperationHoursRecord, OperationHoursSource } from "models/OperationHoursRecord"
import { Grid, GridItem } from "components/Grid"
import InputNumber from "components/InputNumber/InputNumber"
import ValidationService, { validationRules } from "services/ValidationService"
import { IValidationRule } from "@/types"
import CustomError from "models/CustomError"
import ValidationMessage from "components/ValidationMessage/ValidationMessage"
import InputDateTime from "components/InputDateTime/InputDateTime"
import OperationHoursRecordType from "./OperationHoursRecordType"
import InlineControlsCancelSave from "components/InlineControls/InlineControlsCancelSave"
import Text from "components/Text/Text"
import FlexibleContent from "components/FlexibleContent/FlexibleContent"
import Spinner from "components/Spinner/Spinner"
import SaveRecordConfirmModal from "./SaveRecordConfirmModal"

interface IRecordedOperationHoursModalProps {
    recordedOperationHours?: OperationHoursRecord[] | null
    newOperationHours?: number | null
    newOperationHoursTime?: Dayjs | null
    onClose(): void
    onAdd?(data: ICreateOperationHoursRecord): Promise<void> | void
    onCancel?(): void
}

const RecordedOperationHoursModal: FC<IRecordedOperationHoursModalProps> = ({
    recordedOperationHours,
    newOperationHours,
    newOperationHoursTime,
    onClose,
    onAdd,
    onCancel,
}) => {
    const { t } = useTranslation()
    const [isAdding, setIsAdding] = useState(typeof newOperationHours === "number" || !!newOperationHoursTime)
    const [isSubmitted, setIsSubmitted] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [operationHours, operationHoursNumber, setOperationHours] = useInputNumber(newOperationHours ?? "")
    const [operationHoursTime, setOperationHoursTime] = useState(newOperationHoursTime || null)
    const [showSaveRecordConfirmModal, setShowSaveRecordConfirmModal] = useState(false)

    const validationErrors = useMemo((): Record<string, CustomError> => {
        const validationFields: [string, IValidationRule, any][] = [
            ["operationHours", { ...validationRules.asset.operationHours, required: true }, operationHours],
            ["operationHoursTime", { required: true }, operationHoursTime],
        ]

        return ValidationService.validateMultiple(validationFields)
    }, [operationHours, operationHoursTime])

    function handleCancel() {
        onCancel?.()
        setIsAdding(false)
    }

    async function handleSave() {
        if (!onAdd) return

        setIsSubmitted(true)

        if (Object.keys(validationErrors).length) {
            return
        }

        setShowSaveRecordConfirmModal(true)
    }

    async function handleSaveConfirm() {
        if (!onAdd) return

        setShowSaveRecordConfirmModal(false)

        if (typeof operationHoursNumber !== "number"
            || !operationHoursTime) {
            return
        }

        setIsSubmitting(true)
        try {
            await onAdd({
                operationHours: operationHoursNumber,
                operationHoursTime: operationHoursTime,
            })
            setIsAdding(false)
            setIsSubmitted(false)
        } catch(e) {
            toastMessages.showSystemError()
        } finally {
            setIsSubmitting(false)
        }
    }

    function handleOperationHoursChange(val: string) {
        if (val && !operationHours && !operationHoursTime) {
            setOperationHoursTime(dayjs().startOf('minute'))
        }
        setOperationHours(val)
    }

    return (
        <>
            <Modal
                title={t('asset.recordedOperatingHours')}
                maxWidth="1050px"
                onClose={onClose}
                footer={
                    <Flex alignItems="center" justifyContent="flex-end" gap="2.4rem">
                        {!!onAdd &&
                            <Button
                                theme="link"
                                label={t('asset.addRecord')}
                                onClick={() => setIsAdding(true)}
                                disabled={!recordedOperationHours || isAdding}
                            />
                        }
                        <Button
                            theme="secondary"
                            label={t('common.cancel')}
                            onClick={onClose}
                        />
                    </Flex>
                }>
                {!!recordedOperationHours
                    ? ((recordedOperationHours.length || isAdding)
                        ?<div className="overflow-auto round-sm border-grey-200 h-100">
                            <Grid columns={`1fr 1fr 1fr ${onAdd && isAdding ? ' max-content' : ''}`}>
                                <GridItem className="bg-grey-100 px-2 py-1 sticky-top z-index-2">
                                    <Text type="pSmallest" className="font-bold">{t('asset.operatingHours')}</Text>
                                </GridItem>
                                <GridItem className="bg-grey-100 px-2 py-1 border-left-grey-200 sticky-top z-index-2">
                                    <Text type="pSmallest" className="font-bold">{t('asset.recordedOnBy')}</Text>
                                </GridItem>
                                <GridItem className="bg-grey-100 px-2 py-1 border-left-grey-200 sticky-top z-index-2">
                                    <Text type="pSmallest" className="font-bold">{t('asset.recordedOperatingHoursType')}</Text>
                                </GridItem>
                                {!!onAdd && isAdding && <GridItem className="bg-grey-100 border-left-grey-200 sticky-top z-index-2"></GridItem>}
                                {isAdding &&
                                    <>
                                        <GridItem className="p-1 border-top-grey-200">
                                            <InputNumber
                                                value={operationHours}
                                                isSubmitted={isSubmitted}
                                                isInvalid={isSubmitted && ValidationService.isFieldInvalid(validationErrors, 'operationHours')}
                                                onChange={handleOperationHoursChange}
                                                after={<div className="px-2">{t('common.hours')}</div>}
                                                disabled={!onAdd || isSubmitting}
                                                autoFocus
                                            />
                                            {isSubmitted && ValidationService.hasFieldErrorMessage(validationErrors, 'operationHours') &&
                                                <ValidationMessage>{validationErrors.operationHours.message}</ValidationMessage>
                                            }
                                        </GridItem>
                                        <GridItem className="p-1 border-left-grey-200 border-top-grey-200">
                                            <InputDateTime
                                                type="datetime"
                                                date={operationHoursTime}
                                                maxDate={dayjs()}
                                                onDateChange={setOperationHoursTime}
                                                isInvalid={isSubmitted && ValidationService.isFieldInvalid(validationErrors, 'operationHoursTime')}
                                                disabled={!onAdd || isSubmitting}
                                                clearButton
                                            />
                                            {isSubmitted && ValidationService.hasFieldErrorMessage(validationErrors, 'operationHoursTime') &&
                                                <ValidationMessage>{validationErrors.operationHoursTime.message}</ValidationMessage>
                                            }
                                        </GridItem>
                                        <GridItem className="p-2 border-left-grey-200 border-top-grey-200">
                                            <OperationHoursRecordType source={OperationHoursSource.manual} />
                                        </GridItem>
                                        {!!onAdd &&
                                            <GridItem className="p-2 border-left-grey-200 border-top-grey-200">
                                                <InlineControlsCancelSave
                                                    onCancel={handleCancel}
                                                    onSave={handleSave}
                                                    disabled={isSubmitting}
                                                />
                                            </GridItem>
                                        }
                                    </>
                                }
                                {recordedOperationHours.map((record, recordIdx) => (
                                    <Fragment key={recordIdx}>
                                        <GridItem className="p-2 border-top-grey-200">
                                            {t('common.xHours', { count: record.operationHours })}
                                        </GridItem>
                                        <GridItem className="p-2 color-grey-500 border-left-grey-200 border-top-grey-200">
                                            {record.recordedOn.format(t('config.dateLongTimeFormat'))}
                                            {!!record.recordedByEmail &&
                                                <Text type="pSmallest">
                                                    <FlexibleContent text={record.recordedByEmail} />
                                                </Text>
                                            }
                                        </GridItem>
                                        <GridItem className="p-2 border-left-grey-200 border-top-grey-200">
                                            {!!record.source &&
                                                <OperationHoursRecordType
                                                    source={record.source}
                                                    reservationId={record.reservationId}
                                                    reservationNumber={record.reservationNumber}
                                                />
                                            }
                                        </GridItem>
                                        {!!onAdd && isAdding &&
                                            <GridItem className="border-left-grey-200 border-top-grey-200"></GridItem>
                                        }
                                    </Fragment>
                                ))}
                            </Grid>
                        </div>
                        : <div className="py-6 text-center">{t('common.noItemsAddedYet')}</div>
                    )
                    : <Spinner className="py-8" />
                }
            </Modal>
            {showSaveRecordConfirmModal && !!recordedOperationHours && typeof operationHoursNumber === 'number' && !!operationHoursTime &&
                <SaveRecordConfirmModal
                    operationHours={operationHoursNumber}
                    operationHoursTime={operationHoursTime}
                    recordedOperationHours={recordedOperationHours}
                    onClose={() => setShowSaveRecordConfirmModal(false)}
                    onConfirm={handleSaveConfirm}
                />
            }
        </>
    )
}

export default RecordedOperationHoursModal
